import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import {
  ChartBuilderAddittionalDimensionSelector,
  ChartBuilderAvailableMetrics,
  ChartBuilderDisplayOptions,
  ChartBuilderGranularity,
  ChartBuilderSidebarBlock,
  ChartBuilderSidebarContainer,
  ChartBuilderStatCardOptions,
} from "../";
import ChartBuilderYAxis from "./ChartBuilderYAxis";
import ChartBuilderXAxis from "./ChartBuilderXAxis";
import { ChartBuilderAttributes } from "../";
import { useNotification, useSelectedProject } from "Hooks";
import ChartBuilderName from "./ChartBuilderName";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useReportBuilderContext } from "Providers/ReportBuilderProvider";
import { ChartBuilderChartType } from "../";
import {
  ApiACLRole,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiChartYAxisKey,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { ChartBuilderOrderLimit } from "../";
import produce from "immer";
import { enumToArray } from "Helpers/enumToText";
import {
  filterNonTrendDimensions,
  getAutoOrderDimension,
  metricIsCompatible,
} from "features/analytics";
import { useDebounce, usePrevious, useUpdateEffect } from "react-use";
import useArray from "Hooks/useArray";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import AccessLevel from "Components/AccessLevel/AccessLevel";
import { generateChart } from "services/ai";
import { AccordianChartBuilderSidebarBlock } from "./ChartBuilderSidebarBlock";
import DebouncedTextField from "features/common/components/DebouncedTextField";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";
import { ChartBuilderSection } from "./ChartBuilder";

interface IChartBuilderSnapshotProps {
  sections?: ChartBuilderSection[];
  headerSlot?: React.ReactNode;
}

function ChartBuilderSnapshot({
  sections,
  headerSlot,
}: IChartBuilderSnapshotProps) {
  const metricToName = useMetricName();
  const { selectedProject } = useSelectedProject();
  const { chart, setChart } = useReportBuilderContext();
  const prevChart = usePrevious(chart);
  const { showInfoNotification, showErrorNotification } = useNotification();
  const [prompt, setPrompt] = useState("");
  // removed metrics
  const { push, clear, array, forEachUnique } = useArray<ApiMetric>([]);
  const [loading, setLoading] = useState(false);

  // Removed onDragEnd

  useEffect(() => {
    setChart((chart) =>
      produce(chart, (draft) => {
        // set 3 empty metrics for bubble, 1 empty for rest
        const idx = draft.yAxisKeys.findIndex((y) => {
          if (typeof y === "string") {
            return false;
          }
          return y.key === "l";
        });

        const y = draft.yAxisKeys[idx] as ApiChartYAxisKey;
        if (y) {
          const f = y.fields || [];
          // set target metrics length
          switch (chart.type) {
            case ApiChartType.BUBBLE:
              let fields = ["", "", ""];
              if (idx >= 0) {
                fields = [f[0] || "", f[1] || "", f[2] || ""];
                draft.yAxisKeys = [
                  {
                    key: "l",
                    fields,
                  },
                ];
              }
              break;
            case ApiChartType.TABLE:
              draft.yAxisKeys = [
                {
                  key: "l",
                  fields: draft.yAxisKeys
                    .map((v) =>
                      typeof v === "string"
                        ? v
                        : (v.fields || []).map((fv) => fv || ""),
                    )
                    .flat(),
                },
              ];
              break;
            case ApiChartType.PIE:
            case ApiChartType.TREE_MAP:
              draft.yAxisKeys = [
                {
                  key: "l",
                  fields: [f[0] || ""],
                },
              ];
              break;
            case ApiChartType.STAT_CARD:
              draft.yAxisKeys = [
                {
                  key: "l",
                  fields:
                    f.length >= 2 ? [f[0] || "", f[1] || ""] : [f[0] || ""],
                },
              ];
              break;
            default:
              if (idx >= 0) {
                y.fields = (y.fields || []).filter((f) => f !== "");
                if (y.fields.length === 0) {
                  y.fields = [""];
                }
              }
              break;
          }
        }

        //  set target dimensions length
        // only tables and graph can have trend dimensions, if not that type remove them
        const d = draft.dimension || [];
        const nonTrend = filterNonTrendDimensions(d as ApiDimension[]);

        switch (chart.type) {
          case ApiChartType.TREE_MAP:
            draft.dimension = [nonTrend[0] || "", nonTrend[1] || ""];
            break;
          case ApiChartType.PIE:
          case ApiChartType.RADAR:
            draft.dimension = [nonTrend[0] || ""];
            break;
          case ApiChartType.STAT_CARD:
            draft.dimension = [];
            break;
          case ApiChartType.GRAPH:
            if (chart.template === ApiChartTemplate.TREND) {
              draft.dimension = [d[0] || "", nonTrend[0] || ""];
            } else {
              draft.dimension = [nonTrend.length > 0 ? nonTrend[0] : ""];
            }
            break;

          default:
            break;
        }
      }),
    );
  }, [chart.type, chart.template, setChart]);

  useUpdateEffect(() => {
    // clear removed metrics
    clear();
    const autoDimension = getAutoOrderDimension(
      chart.dimension as ApiDimension[],
    );
    setChart((chart) =>
      produce(chart, (draft) => {
        draft.yAxisKeys.forEach((_, i) => {
          const y = draft.yAxisKeys[i] as ApiChartYAxisKey;
          y.fields = (y.fields || []).filter((f) => {
            if (
              metricIsCompatible(
                f as ApiMetric,
                draft.dimension as ApiDimension[],
              )
            ) {
              return true;
            }
            // push to remove metrics
            push(f as ApiMetric);
            // remove from chart
            if (y.chart) {
              delete y.chart[f];
            }
            return false;
          });
        });

        // populate order by based on dimension
        if (autoDimension) {
          draft.orderBy = autoDimension.toString();
        }
      }),
    );
  }, [chart.dimension]);

  useDebounce(
    () => {
      forEachUnique((metric) => {
        showInfoNotification(
          `${metricToName(
            metric,
          )} removed due to incomptability with selected dimensions`,
        );
      });
    },
    300,
    [array],
  );

  const dimensions: ApiDimension[] = useMemo(() => {
    let filtered: ApiDimension[] = [
      ApiDimension.DIMENSION_NOT_SET,
      ApiDimension.DIMENSION_PRODUCT,
      ApiDimension.DIMENSION_JOB,
      ApiDimension.DIMENSION_LOCATION_TAGGER,
    ];
    return filterNonTrendDimensions(enumToArray(ApiDimension)).filter(
      (d) => !filtered.includes(d),
    );
  }, []);

  useEffect(() => {
    if (!chart?.includeSubdomains && chart.dimension !== prevChart?.dimension) {
      setChart((chart) =>
        produce(chart, (draft) => {
          draft.includeSubdomains = chart.dimension?.includes(
            ApiDimension.DIMENSION_DOMAIN,
          );
        }),
      );
    }
  }, [chart, setChart, prevChart]);

  const displayFeatures: ("rows" | "total")[] | undefined = useMemo(() => {
    return chart.type === ApiChartType.GRAPH
      ? []
      : chart.type === ApiChartType.TABLE
        ? ["rows", "total"]
        : chart.type === ApiChartType.PIE
          ? ["total"]
          : [
                ApiChartType.BUBBLE,
                ApiChartType.RADAR,
                ApiChartType.TREE_MAP,
              ].includes(chart.type!)
            ? []
            : undefined;
  }, [chart.type]);

  const generateChartWithAI = useCallback(async () => {
    try {
      setLoading(true);
      const response = await generateChart(
        prompt,
        selectedProject?.id as number,
      );
      if (response && Object.keys(response).length > 0) {
        setChart({ ...response, id: chart.id });
      } else {
        showErrorNotification("Failed to generate chart");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [prompt, selectedProject?.id, setChart, showErrorNotification, chart.id]);

  const shouldShowSection = useCallback(
    (type) => {
      return !sections || sections.includes(type);
    },
    [sections],
  );

  if (!selectedProject) {
    return <CircularProgress />;
  }

  return (
    <ChartBuilderSidebarContainer>
      {headerSlot ? (
        headerSlot
      ) : (
        <ChartBuilderSidebarBlock>
          <Typography variant="h3">Snapshot Chart Setup</Typography>
        </ChartBuilderSidebarBlock>
      )}
      {shouldShowSection("name") && <ChartBuilderName />}

      {shouldShowSection("ai") && (
        <AccessLevel role={ApiACLRole.SUPER_ADMIN} write>
          <AccordianChartBuilderSidebarBlock
            title={"Generate with AI"}
            subTitle={"What would you like to see?"}
          >
            <>
              <Stack spacing={1}>
                <DebouncedTextField
                  label="Prompt"
                  multiline
                  rows={4}
                  maxRows={10}
                  value={prompt}
                  onChange={(v) => setPrompt(v)}
                />
                <Button
                  size="medium"
                  variant="contained"
                  color="primary"
                  onClick={generateChartWithAI}
                  disabled={!prompt || loading}
                  endIcon={
                    loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AiIcon height={18} width={18} />
                    )
                  }
                >
                  Generate
                </Button>
              </Stack>
            </>
          </AccordianChartBuilderSidebarBlock>
        </AccessLevel>
      )}
      {shouldShowSection("type") && <ChartBuilderChartType />}
      {shouldShowSection("granularity") &&
        [ApiChartType.GRAPH, ApiChartType.TABLE].includes(
          chart.type || ApiChartType.UNDEFINED,
        ) && <ChartBuilderGranularity />}

      {shouldShowSection("xAxis") && chart.type !== ApiChartType.STAT_CARD && (
        <>
          <ChartBuilderXAxis
            dimensions={dimensions}
            noDrag={chart.type !== ApiChartType.TABLE}
            allowDelete={[ApiChartType.TABLE, ApiChartType.GRAPH].includes(
              chart.type!,
            )}
            isMultiple={chart.type === ApiChartType.TABLE}
            title={
              chart.type === ApiChartType.PIE
                ? "Pie Dimension"
                : chart.type === ApiChartType.BUBBLE
                  ? "Bubble Dimension"
                  : chart.type === ApiChartType.RADAR
                    ? "Radar Points"
                    : chart.type === ApiChartType.TREE_MAP
                      ? "Treemap Dimension"
                      : chart.type === ApiChartType.TABLE
                        ? "Rows / Dimension"
                        : undefined
            }
            subTitle={
              chart.type === ApiChartType.PIE
                ? `Select a dimension that the pie chart shall be sliced into`
                : chart.type === ApiChartType.BUBBLE
                  ? "Select a dimension for the focus of the bubble chart"
                  : chart.type === ApiChartType.RADAR
                    ? "Select a dimension for each point of the radar chart"
                    : chart.type === ApiChartType.TREE_MAP
                      ? "Choose a dimension for each square on the treemap. Use the first field to select a category, such as audience, to group your results."
                      : chart.type === ApiChartType.TABLE
                        ? "These are the values you wish to group your rows by, e.g session date or channel name"
                        : undefined
            }
            project={selectedProject}
          />

          <ChartBuilderAddittionalDimensionSelector
            dimensions={dimensions}
            chart={chart}
            setChart={setChart}
          />
        </>
      )}

      {shouldShowSection("yAxis") && (
        <ChartBuilderYAxis
          maxMetrics={chart.type === ApiChartType.STAT_CARD ? 2 : undefined}
          noDrag={
            ![
              ApiChartType.TABLE,
              ApiChartType.PIE,
              ApiChartType.STAT_CARD,
            ].includes(chart.type || ApiChartType.UNDEFINED)
          }
          y={"l"}
          noControls={[
            ApiChartType.PIE,
            ApiChartType.BUBBLE,
            ApiChartType.RADAR,
            ApiChartType.TREE_MAP,
            ApiChartType.TABLE,
            ApiChartType.STAT_CARD,
          ].includes(chart.type || ApiChartType.UNDEFINED)}
          noRemove={
            chart.type === ApiChartType.PIE ||
            chart.type === ApiChartType.BUBBLE ||
            chart.type === ApiChartType.TREE_MAP
          }
          title={
            chart.type === ApiChartType.PIE
              ? "Pie Metric"
              : chart.type === ApiChartType.BUBBLE
                ? "Bubble Metrics"
                : chart.type === ApiChartType.RADAR
                  ? "Radar Metrics"
                  : chart.type === ApiChartType.TREE_MAP
                    ? "Treemap Metric"
                    : chart.type === ApiChartType.TABLE
                      ? "Columns / Metrics"
                      : undefined
          }
          subTitle={
            chart.type === ApiChartType.PIE
              ? `Select a Metric which each section will represent`
              : chart.type === ApiChartType.BUBBLE
                ? "Select 3 metrics for bubble chart, 1st is X, 2nd is Y and 3rd is bubble size"
                : chart.type === ApiChartType.RADAR
                  ? "Select a or multiple metrics that will be laid out on the radar chart"
                  : chart.type === ApiChartType.TREE_MAP
                    ? "Select a Metric that the size of each square will represent"
                    : chart.type === ApiChartType.TABLE
                      ? "These are the values you want per dimension / row"
                      : chart.type === ApiChartType.STAT_CARD
                        ? "Select Which metric you want to display as you primary stat"
                        : undefined
          }
        />
      )}

      {shouldShowSection("yAxis") && chart.type !== ApiChartType.STAT_CARD && (
        <ChartBuilderAvailableMetrics
          chart={chart}
          setChart={setChart}
          y="l"
        />
      )}

      {shouldShowSection("orderLimit") &&
        chart.type !== ApiChartType.STAT_CARD && (
          <>
            {[ApiChartType.GRAPH].includes(
              chart.type || ApiChartType.UNDEFINED,
            ) && (
              <ChartBuilderYAxis
                noDrag
                y={"r"}
              />
            )}

            <ChartBuilderOrderLimit
              hideOrder={chart.type === ApiChartType.TREE_MAP}
              maxLimit={chart.type === ApiChartType.RADAR ? 10 : undefined}
              chart={chart}
              setChart={setChart}
            />

            <ChartBuilderDisplayOptions features={displayFeatures} />
          </>
        )}
      {shouldShowSection("displayOptions") &&
        chart.type === ApiChartType.STAT_CARD && (
          <ChartBuilderStatCardOptions />
        )}
      {shouldShowSection("attributes") && (
        <ChartBuilderAttributes
          attributes={chart.attributes}
          setAttributes={(attributes) =>
            setChart((chart) =>
              produce(chart, (draft) => {
                draft.attributes = attributes as ApiChartAttribute[];
              }),
            )
          }
        />
      )}
    </ChartBuilderSidebarContainer>
  );
}

export default ChartBuilderSnapshot;
